import { can } from 'app/helpers/can';
import { isAgency } from 'app/helpers/userRoles';
import { getLoginAsAccessToken } from 'app/services/jwtService';
import { RootState } from 'app/store/rootReducer';
import { setSidebarLayoutStyle } from 'app/store/ui';
import FetchThreads from 'app/ui/modules/messaging/components/fetch-threads/FetchThreads';
import clsx from 'clsx';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';

import { Box, SxProps, Theme, useMediaQuery, useTheme } from '@mui/material';

import AppSuspense from '../../ui/components/AppSuspense';
import LayoutErrorBoundary from '../errors/LayoutErrorBoundary';
import LoginAsOverlay from './components/LoginAsOverlay';
import NavbarWrapperLayout from './components/NavbarWrapperLayout';
import ToolbarLayout from './components/ToolbarLayout';

const getStyles = (theme: Theme): { [key: string]: SxProps } => {
  return {
    root: {
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      position: 'absolute',
      display: 'flex',
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
    },
    content: {
      position: 'relative',
      display: 'flex',
      overflow: 'auto',
      flex: '1 1 auto',
      flexDirection: 'column',
      width: '100%',
      WebkitOverflowScrolling: 'touch',
      zIndex: 2,
      paddingTop: 8,
      '& > div': {
        maxWidth: theme.breakpoints.values['lg'],
        flexGrow: 1,
      },
      '&.forced-no-sidebar > div': { margin: '0 auto', width: '100%' },
      '&.messaging-opened > div': { height: '100%' },
      '&.gallery-opened ': { zIndex: 10000 },
      '&.toolbar-mobile-opened': {
        paddingBottom: 10,
      },
    },
    layoutWrapper: {
      position: 'relative',
      display: 'flex',
      width: '100%',
      minWidth: '320px',
    },
  };
};

export default function DefaultLayout() {
  const { user } = useSelector((state: RootState) => state.auth);
  const { isWithoutSidebar, isGalleryOpened } = useSelector((state: RootState) => state.ui);

  const theme = useTheme();
  const matchesMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const styles = getStyles(theme);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setSidebarLayoutStyle());
  }, [dispatch]);

  const loginAsToken = getLoginAsAccessToken();

  return (
    <>
      <Box sx={styles.root}>
        {!isWithoutSidebar && user && <NavbarWrapperLayout />}
        <Box sx={styles.layoutWrapper}>
          <ToolbarLayout isUserLogged={user ? true : false} isWithoutSidebar={isWithoutSidebar || !user} />
          <Box
            component="main"
            id="main-scroll-container"
            sx={styles.content}
            className={clsx(
              matchesMobile && 'toolbar-mobile-opened',
              (isWithoutSidebar || !user) && 'forced-no-sidebar',
              isGalleryOpened && 'gallery-opened'
            )}
          >
            <LayoutErrorBoundary>
              <AppSuspense>
                <div>
                  <Outlet />
                </div>
              </AppSuspense>
            </LayoutErrorBoundary>
          </Box>
          {user && (!isAgency(user) || (isAgency(user) && can(user, 'manage_projects'))) && <FetchThreads />}
        </Box>
      </Box>
      {loginAsToken && user?.role !== 'backoffice' && <LoginAsOverlay />}
    </>
  );
}
